import React, { createContext, useContext } from 'react';
import { PureAbility } from '@casl/ability';
import { defineAbilitiesFor } from './defineAbilities';

type Actions = 'read' | 'create' | 'update' | 'delete' | 'manage';
type Subjects = 'AdminPage' | 'ManagerPage' | 'UserPage' | 'all';

type AbilitiesContextType = PureAbility<[Actions, Subjects]>;

const AbilitiesContext = createContext<AbilitiesContextType | null>(null);

/**
 * Componente proveedor que crea y provee las habilidades (permissions)
 * basadas en el rol del usuario.
 * 
 * @param role - El rol del usuario para definir sus permisos.
 * @param children - Componentes hijos que tendrán acceso al contexto de habilidades.
 * @returns Provider que envuelve a los hijos con el contexto de habilidades.
 */
export const AbilityProvider: React.FC<{ role: string; children: React.ReactNode }> = ({ role, children }) => {
    const ability = defineAbilitiesFor(role);
    return (
        <AbilitiesContext.Provider value={ability}>
            {children}
        </AbilitiesContext.Provider>
    );
};

/**
 * Hook personalizado para acceder al contexto de habilidades.
 * 
 * @throws Error si se usa fuera de un AbilityProvider.
 * @returns El objeto de habilidades (ability) definido en el contexto.
 */
export const useAbility = (): AbilitiesContextType => {
    const context = useContext(AbilitiesContext);
    if (!context) {
        throw new Error('useAbility must be used within an AbilityProvider');
    }
    return context;
};
